import { useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useEntity } from '../contexts/EntityContext';
import { extractPlatformUser, type Auth0User } from '../utils/auth0UserUtils';
import { setTraceUserAttributes } from '../services/logger/traceAttributes';
import { setFaroUser } from '../services/logger/faroUser';

/**
 * Keeps Faro identity in sync with the current Auth0 user and active entity:
 *
 *  - Faro user meta (`setUser`) → powers the User association in the Faro/Frontend
 *    Observability dashboard and is auto-attached to logs, events and spans:
 *      user.id (platform_user uuid), user.email, attribute entity_friendly_id
 *  - Trace span attributes → tag fetch/XHR spans exported to Tempo:
 *      user.email, user.id, entity.friendly_id
 *
 * Both stores are plain module singletons read imperatively (by the span hooks and
 * the captured Faro api), so this intentionally avoids useEffect. A ref-based
 * fingerprint guard ensures we only re-apply when the identity actually changes.
 *
 * Must be rendered inside `<EntityProvider>` and an authenticated tree.
 */
export function useTraceUserAttributes(): void {
  const { user } = useAuth0<Auth0User>();
  const { activeEntity } = useEntity();
  const lastAppliedRef = useRef<string>('');

  const email = user?.email;
  const uuid = extractPlatformUser(user)?.uuid;
  const entityFriendlyId = activeEntity?.friendlyId;

  const fingerprint = `${uuid ?? ''}|${email ?? ''}|${entityFriendlyId ?? ''}`;
  if (fingerprint !== lastAppliedRef.current) {
    lastAppliedRef.current = fingerprint;
    setTraceUserAttributes({ email, uuid, entityFriendlyId });
    setFaroUser({ id: uuid, email, entityFriendlyId });
  }
}
